import React, { useRef, useState, useCallback } from 'react';
import { Formik } from 'formik';
import Image from 'next/image';
import { useAlert } from 'components';

import { ModalMain } from '../main';
import { InputField, Loader } from 'common';
import { IUser } from 'constants/types';
import { Button } from 'common/new-button';
import { model, validationSchema, initialValues } from './config';
import { Icon } from 'common/icon';
import { useClickOutside } from 'hooks/useClickOutside';
import { handleMutation } from 'utils/handleMutation';

import { useUserUploadPhotoMutation } from 'hooks/api/users';

import styles from './styles.module.css';
import { Analytics } from 'constants/analytics';

interface Props {
  data?: IUser;
  isVisible: boolean;
  onClose?: () => void;
  confirmOnClick?: () => void;
}

export const EditUserPhotoModal = ({ data, isVisible, onClose }: Props) => {
  const alert = useAlert();
  const modalRef = useRef<HTMLDivElement | null>(null);

  useClickOutside({ onClick: onClose, ref: modalRef });

  const [uploadPhoto, uploadPhotoMutation] = useUserUploadPhotoMutation();

  const isLoadingPhoto = uploadPhotoMutation.isLoading;

  const [avatar, setAvatar] = useState(data?.imageUrl ?? '');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadedFileName, setUploadedFileName] = useState('');

  const onSuccessPhoto = useCallback(() => {
    alert.success('Upload photo successfully added');
  }, [alert]);

  const onErrorPhoto = useCallback(
    (error: any) => {
      alert.error(error?.data?.error || 'Error');
    },
    [alert]
  );

  handleMutation({
    ...uploadPhotoMutation,
    onSuccess: onSuccessPhoto,
    onError: onErrorPhoto,
  });

  const onSubmit = async () => {
    if (uploadFile) {
      await uploadPhoto({
        image: uploadFile,
      });
      if (onClose) {
        onClose();
      }
    }
  };

  const onChangeUploadPhoto = async (files: FileList | null) => {
    if (!files) return;
    const file = files[0];
    setUploadedFileName(file.name);
    setAvatar(URL.createObjectURL(file));
    setUploadFile(file);
  };

  return (
    <ModalMain
      title=""
      footer=""
      width={260}
      targetRef={modalRef}
      isVisible={isVisible}
      closeOnClick={onClose}
    >
      <Formik
        onSubmit={onSubmit}
        initialValues={initialValues()}
        validationSchema={() => validationSchema()}
      >
        {({ submitForm, isSubmitting }) => (
          <div className={styles.body}>
            <div className={styles.wrapper_header}>
              <span>{'Update Photo'}</span>
              <div className={styles.iconClose} onClick={onClose}>
                <Icon.Close />
              </div>
            </div>
            <div className={styles.wrapper_content}>
              <div className={styles.update_photo}>
                <InputField
                  {...model.userPhoto}
                  onChange={(e) => onChangeUploadPhoto(e.target.files)}
                />
                <div className={styles.wrapper_image}>
                  <Image
                    width={30}
                    alt="user"
                    height={30}
                    src={avatar}
                    className={styles.image}
                  />
                  <span className={styles.image_name}>
                    {isLoadingPhoto ? (
                      <Loader size="big" />
                    ) : (
                      uploadedFileName || 'Update avatar...'
                    )}
                  </span>
                </div>
              </div>
              <div className={styles.submitButton}>
                <Button
                  id={Analytics.user.editAvatar}
                  onClick={submitForm}
                  disabled={isSubmitting}
                  text={isLoadingPhoto ? 'Loading...' : 'Submit'}
                />
              </div>
              <div className={styles.cancelButton}>
                <Button text="Cancel" onClick={onClose} />
              </div>
            </div>
          </div>
        )}
      </Formik>
    </ModalMain>
  );
};
